<template>
  <div
    ref="pieContainer"
    class="pie-container"
  >
    <highcharts 
      ref="highchart"
      :options="pieChartOptions"
      :class="{'highcharts-custom--disable-drilldown': !enableDrilldown, 'highcharts-custom--disable-drillup': chartDrilleddownTo === -1}"
    />
  </div>
</template>

<script>
import { cloneDeep, isEmpty } from 'lodash';

import chartMixinFactory from '../mixins/chartMixinFactory';
import { defaultPieChartOptions, addMouseOverListenersToLegends, PIE_LEGEND_MAX_HEIGHT } from '../utils/pieChart';
import { useYAxisForDataType } from '../utils/columnChart';

// TODO: Handle window resizing

// TODO: Refactor this part
const PIE_SIZE = 170;
const PIE_YAXIS_LINE_GAP = 140;
const PIE_XAXIS_GAP = PIE_SIZE * 1.5;
const PIE_LEGEND_LINE_HEIGHT = 20;
const PIE_CHART_LEGEND_MARGIN = 70;
const AVERAGE_LEGEND_WIDTH = 300;

let firstPieOnLinePosition = 100;
const PIE_FIRST_LINE_CENTER_Y_POS = 150;

function calculateChartHeight(data) {
  try {
    var legendPerLine = Math.floor(this.chartWidth / AVERAGE_LEGEND_WIDTH) || 1;
  } catch (error) {
    legendPerLine = 2;
  }

  let legendGroupHeight = PIE_LEGEND_LINE_HEIGHT * Math.ceil(data.length / legendPerLine);
  legendGroupHeight = legendGroupHeight <= PIE_LEGEND_MAX_HEIGHT ? legendGroupHeight : PIE_LEGEND_MAX_HEIGHT;

  const noOfLines = this.selectedParameters && Math.ceil(this.selectedParameters.length / this.piePerLine);

  return (
    (noOfLines * (PIE_YAXIS_LINE_GAP * 2)) +
    legendGroupHeight + 
    PIE_CHART_LEGEND_MARGIN
  );
}

export default {
  name: 'ReportingPieChart',
  mixins: [chartMixinFactory('pie')],
  props: {
    selectedChartType: {
      type: String,
      default: ''
    },
    selectedGroupBy: {
      type: Object,
      default() {
        return {};
      }
    },
  },
  data() {
    return {
      piePerLine: 3,
    };
  },
  computed: {
    defaultTitle() {
      return `${this.groupBy.displayName} Insights`;
    },
    pieChartOptions() {
      if (this.drilleddownTo.length) {
        return {};
      }
      
      let options = cloneDeep(defaultPieChartOptions);
      options.title.text = this.defaultTitle;
      options.chart = {
        ...options.chart,
        events: {
          drilldown: this.onDrilldown,
          drillupall: this.onDrillup,
          redraw: function handleRedrawCallback() {
            addMouseOverListenersToLegends(this);
          }
        },
        height: this.chartHeight,
        width: this.chartWidth
      };

      if (!this.groupBy || !this.selectedParameters) {
        return options;
      }

      for (const parameter of this.selectedParameters) {
        options.series = [
          ...options.series,
          ...getSeries([parameter], this.data, this.groupBy.field, true)
        ];
      }

      options.series = placePies(options.series, this.piePerLine);

      return options;
    },
    chartHeight() {
      return calculateChartHeight.call(this, this.data);
    },
    chartWidth() {
      let piePerLine = this.selectedParameters && this.piePerLine > this.selectedParameters.length ? this.selectedParameters.length : this.piePerLine;
      return piePerLine * PIE_XAXIS_GAP;
    },
  },
  watch: {
    drilleddownData(newValue, oldValue) {
      if (newValue && !isEmpty(newValue)) {
        this.setDrilldownData(newValue, oldValue);
      }
    },
  },
  mounted() {
    if (this.$refs['pieContainer']) {
      this.$refs['pieContainer'].parentElement.style.display = 'block';
      let containerWidth = this.$refs['pieContainer'].clientWidth;

      if (this.selectedChartType === 'table' || (this.selectedGroupBy && this.selectedGroupBy.field !== this.groupBy.field)) {
        this.$refs['pieContainer'].parentElement.style.display = 'none';
      }

      this.piePerLine = Math.floor(containerWidth / PIE_XAXIS_GAP);

      if (this.piePerLine === 1) {
        firstPieOnLinePosition = '50%';
      }
    }
  },
  methods: {
    setDrilldownData(drilleddownData, oldDrilleddownData) {
      if (this.chartDrilleddownTo === this.currentDrilldownLevel && oldDrilleddownData && oldDrilleddownData[this.currentDrilldownItem.parentEntityId]) {
        return this.updateDrilleddownData(drilleddownData[this.currentDrilldownItem.parentEntityId].data);
      }

      let aggregatedData = drilleddownData[this.currentDrilldownItem.parentEntityId].data;
      let chartPoint = drilleddownData[this.currentDrilldownItem.parentEntityId].point;
      let drilldownToType = this.chartDrilldownPath[this.currentDrilldownLevel];
      let series = [];
      let chart = this.$refs['highchart'].chart;

      for (const parameter of this.selectedParameters) {
        series = [
          ...series,
          ...getSeries(
            [parameter],
            aggregatedData,
            drilldownToType.field,
            true,
            chartPoint.name
          )
        ];
      }

      series = placePies(series, this.piePerLine);

      for (const seriesData of series) {
        chart.addSingleSeriesAsDrilldown(chartPoint, seriesData);
      }

      chart.applyDrilldown();

      chart.setSize(null, calculateChartHeight.call(this, aggregatedData));

      this.chartDrilleddownTo++;
    },

    updateDrilleddownData(updatedData) {
      let chart = this.$refs['highchart'].chart;
      let drilldownToType = this.chartDrilldownPath[this.currentDrilldownLevel];

      let series = [];

      for (const parameter of this.selectedParameters) {
        series = [
          ...series,
          ...getSeries(
            [parameter],
            updatedData,
            drilldownToType.field,
            true,
            this.currentDrilldownItem.parentEntityId
          )
        ];
      }

      chart.setSize(null, calculateChartHeight.call(this, updatedData));

      chart.series.forEach(s => {
        s.setData(getSeriesData({ name: s.options.customRingbaParameter.name }, updatedData, drilldownToType.field, true));
      });
    }
  }
};

function placePies(series, piePerLine = 3) {
  const xAxisCenterPoints = [firstPieOnLinePosition];

  for (let index = 1; index < piePerLine; index++) {
    xAxisCenterPoints[index] = xAxisCenterPoints[index - 1] + PIE_XAXIS_GAP;
  }

  series.forEach(function(pie, i) {
    let pieOnLine = Math.floor(i / piePerLine);
    let alignY =
      pieOnLine === 0
        ? PIE_FIRST_LINE_CENTER_Y_POS
        : (2 * pieOnLine + 1) * PIE_YAXIS_LINE_GAP;

    pie.size = PIE_SIZE;
    pie.center = [xAxisCenterPoints[i % piePerLine], alignY];
    pie.showInLegend = i === 0; // Show the points' names in a legend but only for the first pie chart
  });

  return series;
}

function* getSeries(params, data, groupBy, enableDrilldown, seriesId) {
  for (const parameter of params) {
    if (groupBy !== parameter.name) {
      yield {
        id: seriesId,
        name: parameter.title,
        type: 'pie',
        data: getSeriesData(parameter, data, groupBy, enableDrilldown),
        colorByPoint: true,
        dataLabels: {
          enabled: false
        },
        title: {
          text: `<span class="pie-chart-title">${parameter.title}</span>`,
          y: -40,
          verticalAlign: 'top'
        },
        tooltip: useYAxisForDataType[parameter.dataType].tooltip,
        borderColor: 'transparent',
        customRingbaParameter: parameter
      };
    }
  }
}

function getSeriesData(param, data, groupBy, enableDrilldown) {
  let seriesData = [];

  for (const entityData of data) {
    seriesData.push({
      y: entityData[param.name],
      name: entityData[groupBy],
      id: entityData[groupBy],
      sliced: false, // TODO: show slice whenever user hovers over a pie
      drilldown: !!enableDrilldown
    });
  }

  return seriesData;
}
</script>

<style lang="scss" scoped>
.pie-container {
  width: 100%;
  display: flex;
  justify-content: center;
}
</style>